import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import adminSchemaApi from '../api/adminSchemaApi';
import MultiSelectDropdown from '../../components/layout/MultiSelectDropdown1';
import ObjectPermissionMatrix from '../../components/common/ObjectPermissionMatrix';
import {
  allowedObjectsForDisplay,
  applyTelephonyPackageToggle,
  DEFAULT_PLAN_CONFIG,
  isAlwaysMigrateObject,
  isReportOrDashboardStandardTab,
  normalizeAllowedObjects,
  normalizePlanConfig,
  SETUP_OPTIONS,
  STANDARD_OBJECT_OPTIONS,
} from './AdminPlanConfigConstants';
import '../styles/admin-theme.css';

const AdminPlanConfig = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [config, setConfig] = useState(DEFAULT_PLAN_CONFIG);
  const [originalConfig, setOriginalConfig] = useState(DEFAULT_PLAN_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [standardObjects, setStandardObjects] = useState([]);
  const [standardTabs, setStandardTabs] = useState([]);

  const loadPlan = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError('');
      const data = await adminSchemaApi.fetchPlanById(id);
      setPlan(data);
      if (data) {
        const normalized = normalizePlanConfig(data.config);
        setConfig(normalized);
        setOriginalConfig(normalized);
      }
      const packages = data?.config?.enabledPackages ?? [];
      const [objects, tabs] = await Promise.all([
        adminSchemaApi.fetchStandardObjects(packages).catch(() => []),
        adminSchemaApi.fetchStandardTabs(packages).catch(() => []),
      ]);
      setStandardObjects(Array.isArray(objects) ? objects : []);
      setStandardTabs(Array.isArray(tabs) ? tabs : []);
    } catch (err) {
      setError('Failed to load plan.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const handlePackageToggle = useCallback(async (packageKey, checked) => {
    let next = applyTelephonyPackageToggle(config.enabledPackages ?? [], packageKey, checked);
    if (checked && packageKey === 'email_campaign') {
      next = [...new Set([...next, 'watconnect'])];
    }
    if (checked) {
      const packageTabIdsToAdd = (standardTabs || [])
        .filter((t) => String(t.config?.package || '').trim() === packageKey)
        .map((t) => t.id)
        .filter(Boolean);
      setConfig((prev) => ({
        ...prev,
        enabledPackages: next,
        ...(packageKey === 'email_campaign'
          ? {
              allowedObjectsPermission: [...new Set([...(prev.allowedObjectsPermission ?? []), 'email_campaign', 'email_campaign_member'])],
              objectManager: {
                ...prev.objectManager,
                allowedObjects: [...new Set([...(prev.objectManager?.allowedObjects ?? []), 'email_campaign', 'email_campaign_member'])],
              },
            }
          : {}),
        ...(packageTabIdsToAdd.length > 0
          ? { tab: { ...prev.tab, allowedTabs: [...new Set([...(prev.tab?.allowedTabs ?? []), ...packageTabIdsToAdd])] } }
          : {}),
      }));
    } else if (!checked) {
      setConfig((prev) => {
        const packageTabIdsToRemove = new Set(
          (standardTabs || []).filter((t) => String(t.config?.package || '').trim() === packageKey).map((t) => t.id).filter(Boolean)
        );
        const allowedTabs = (prev.tab?.allowedTabs ?? []).filter((id) => !packageTabIdsToRemove.has(id));
        const updates = {
          ...prev,
          enabledPackages: next,
          tab: { ...prev.tab, allowedTabs },
        };
        if (packageKey === 'email_campaign') {
          updates.allowedObjectsPermission = (prev.allowedObjectsPermission ?? []).filter((o) => o !== 'email_campaign' && o !== 'email_campaign_member');
          updates.objectManager = {
            ...prev.objectManager,
            allowedObjects: (prev.objectManager?.allowedObjects ?? []).filter((o) => o !== 'email_campaign' && o !== 'email_campaign_member'),
          };
        }
        return updates;
      });
    } else {
      setConfig((prev) => ({ ...prev, enabledPackages: next }));
    }
    const [objects, tabs] = await Promise.all([
      adminSchemaApi.fetchStandardObjects(next).catch(() => []),
      adminSchemaApi.fetchStandardTabs(next).catch(() => []),
    ]);
    setStandardObjects(Array.isArray(objects) ? objects : []);
    setStandardTabs(Array.isArray(tabs) ? tabs : []);
  }, [config.enabledPackages, standardTabs]);

  React.useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  const handleSave = async () => {
    setError('');
    setMessage('');
    try {
      setSaving(true);
      const toSave = {
        ...config,
        allowedObjectsPermission: normalizeAllowedObjects(config.allowedObjectsPermission ?? []),
        objectManager: {
          ...config.objectManager,
          allowedObjects: normalizeAllowedObjects(config.objectManager?.allowedObjects ?? []),
        },
      };
      await adminSchemaApi.updatePlan(id, { config: toSave });
      setMessage('Plan config saved.');
      setConfig(toSave);
      setOriginalConfig(toSave);
      setEditMode(false);
    } catch (err) {
      setError(err?.message || 'Failed to save plan config.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setConfig(originalConfig);
    setEditMode(false);
    setMessage('');
    setError('');
  };

  const handleAllowedObjectsChange = (section, values) => {
    setConfig((prev) => ({
      ...prev,
      [section]: { ...prev[section], allowedObjects: values },
    }));
  };

  const handleAllowedObjectsPermissionChange = (values) => {
    setConfig((prev) => {
      const currentLeadConversion = prev.leadConversionMapping?.allowedObjects ?? ['account', 'contact', 'opportunity'];
      const nextLeadConversion = currentLeadConversion.filter((o) => values.includes(o));
      const resolved =
        nextLeadConversion.length > 0
          ? nextLeadConversion
          : ['account', 'contact', 'opportunity'].filter((o) => values.includes(o));
      return {
        ...prev,
        allowedObjectsPermission: values,
        objectManager: { ...prev.objectManager, allowedObjects: values },
        leadConversionMapping: { ...prev.leadConversionMapping, allowedObjects: resolved },
      };
    });
  };

  const isReadOnly = !editMode || loading;

  const standardPicklistOptions = useMemo(() => {
    let options;
    if (standardObjects.length > 0) {
      options = standardObjects.map((o) => {
        const name = o.object_name || '';
        const label = name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        return { key: name, value: label || name };
      });
    } else {
      options = STANDARD_OBJECT_OPTIONS.map((o) => ({ key: o.name, value: o.label }));
    }
    return options.filter((o) => !isAlwaysMigrateObject(o.key));
  }, [standardObjects]);

  const leadConversionPicklistOptions = useMemo(() => {
    const allowed = config.allowedObjectsPermission ?? [];
    return standardPicklistOptions.filter((o) => allowed.includes(o.key));
  }, [standardPicklistOptions, config.allowedObjectsPermission]);

  const scoringRulePicklistOptions = useMemo(() => {
    const allowed = config.allowedObjectsPermission ?? [];
    return standardPicklistOptions.filter((o) => allowed.includes(o.key));
  }, [standardPicklistOptions, config.allowedObjectsPermission]);

  const standardTabPicklistOptions = useMemo(() => {
    const allowedObjectNames = config.allowedObjectsPermission ?? [];
    const enabledPackages = config.enabledPackages ?? [];
    return standardTabs
      .filter((t) => {
        const tabPackage = t.config?.package && String(t.config.package).trim();
        const packageOk = !tabPackage || enabledPackages.includes(tabPackage);
        if (!packageOk) return false;
        return (
          isReportOrDashboardStandardTab(t) ||
          !t.object_name ||
          allowedObjectNames.includes(t.object_name)
        );
      })
      .map((t) => {
        const displayName = t.label || (t.name || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || t.name;
        const formatKey = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        const suffixParts = [t.object_name, t.config?.package].filter(Boolean).map(formatKey);
        const label = suffixParts.length > 0 ? `${displayName} (${suffixParts.join(', ')})` : displayName;
        return { key: t.id, value: label };
      });
  }, [standardTabs, config.allowedObjectsPermission, config.enabledPackages]);

  const packageTabIds = useMemo(() => {
    const pkgSet = new Set((config.enabledPackages ?? []).map((p) => String(p).toLowerCase().trim()));
    return (standardTabs || [])
      .filter((t) => t.config?.package && pkgSet.has(String(t.config.package).toLowerCase().trim()))
      .map((t) => t.id)
      .filter(Boolean);
  }, [standardTabs, config.enabledPackages]);

  const tabAllowedTabsSelected = useMemo(() => {
    const validIds = new Set(standardTabPicklistOptions.map((o) => o.key));
    const saved = config.tab?.allowedTabs ?? [];
    const merged = [...new Set([...packageTabIds, ...saved])].filter((id) => validIds.has(id));
    if (merged.length > 0) return merged;
    return standardTabPicklistOptions.map((o) => o.key);
  }, [config.tab?.allowedTabs, standardTabPicklistOptions, packageTabIds]);
  if (!id) {
    navigate('/admin/plans', { replace: true });
    return null;
  }

  return (
    <>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <Link to="/admin/plans" className="btn btn-outline-secondary btn-sm me-2">
            ← Plans
          </Link>
          <Link to={`/admin/plans/${id}/detail`} className="btn btn-outline-info btn-sm me-2">
            Detail & prices
          </Link>
          <Link to={`/admin/plans/${id}/licenses`} className="btn btn-outline-primary btn-sm me-2">
            Licenses
          </Link>
          <h2 className="admin-title mb-1 d-inline">Plan config</h2>
          {plan && (
            <p className="admin-subtitle mb-0">
              {plan.name || plan.code} ({plan.code})
            </p>
          )}
        </div>
        <div className="d-flex gap-2">
          {editMode ? (
            <>
              <Button className="admin-primary-btn" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
              <Button variant="secondary" onClick={handleCancel} disabled={saving}>
                Cancel
              </Button>
            </>
          ) : (
            <Button
              className="admin-primary-btn"
              onClick={() => setEditMode(true)}
              disabled={loading || saving}
            >
              Edit
            </Button>
          )}
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {message && <Alert variant="success">{message}</Alert>}

      {loading ? (
        <div className="admin-subtitle">Loading...</div>
      ) : !plan ? (
        <div className="admin-subtitle">Plan not found.</div>
      ) : (
        <>
          {/* Setup on top: when enabled, user can edit Tab, Field, Layout, Workflow, Scoring Rule, Assignment Rule, Web Forms, Search Views, Lead Conversion and Migration, Object Manager */}
          <Row className="g-3">
            <Col xs={12}>
              <Card className="p-3">
                <h5>Setup</h5>
                <Form.Check
                  type="switch"
                  label="Show setup"
                  checked={config.setup.showSetup !== false}
                  onChange={(e) =>
                    setConfig((prev) => ({
                      ...prev,
                      setup: { ...prev.setup, showSetup: e.target.checked },
                    }))
                  }
                  disabled={isReadOnly}
                  className="mb-2"
                />
                <Form.Text className="d-block mb-0 text-muted small">
                  When disabled, users only get access to the objects below (no setup menu). When enabled, they can edit Tab, Field, Layout, Workflow, Scoring Rule, Assignment Rule, Web Forms, Search Views, Lead Conversion and Migration, and Object Manager.
                </Form.Text>
              </Card>
            </Col>
          </Row>

          <Row className="g-3">
            <Col md={6}>
              <Card className="p-3">
                <h5>Packages</h5>
                <Form.Text className="d-block mb-2 text-muted small">
                  Enable packages for this plan. Standard objects that belong to a package only appear in the list below when that package is enabled.
                  TeleCMI and Callyzer are mutually exclusive.
                </Form.Text>
                <Form.Check
                  type="switch"
                  id="plan-package-watconnect"
                  label="WatConnect"
                  checked={Array.isArray(config.enabledPackages) && config.enabledPackages.includes('watconnect')}
                  onChange={(e) => handlePackageToggle('watconnect', e.target.checked)}
                  disabled={isReadOnly}
                />
                <Form.Check
                  type="switch"
                  id="plan-package-watconnect-email-campaign"
                  className="mt-2"
                  label="Email Campaign"
                  checked={Array.isArray(config.enabledPackages) && config.enabledPackages.includes('email_campaign')}
                  onChange={(e) => handlePackageToggle('email_campaign', e.target.checked)}
                  disabled={isReadOnly}
                />
                <Form.Text className="text-muted small d-block mt-1">
                  When enabled, users can send email from list view; email_campaign and email_campaign_member are available.
                </Form.Text>
                <Form.Check
                  type="switch"
                  id="plan-package-telecmi"
                  className="mt-3"
                  label="TeleCMI (click-to-call)"
                  checked={Array.isArray(config.enabledPackages) && config.enabledPackages.includes('telecmi')}
                  onChange={(e) => handlePackageToggle('telecmi', e.target.checked)}
                  disabled={isReadOnly}
                />
                <Form.Check
                  type="switch"
                  id="plan-package-callyzer"
                  className="mt-2"
                  label="Callyzer"
                  checked={Array.isArray(config.enabledPackages) && config.enabledPackages.includes('callyzer')}
                  onChange={(e) => handlePackageToggle('callyzer', e.target.checked)}
                  disabled={isReadOnly}
                />
                <Form.Text className="text-muted small d-block mt-1">
                  Only one of TeleCMI or Callyzer can be on; turning one on turns the other off.
                </Form.Text>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="p-3">
                <h5>Allowed Objects Permission</h5>
                <Form.Text className="d-block mb-2 text-muted small">
                  Objects the user can access. Only these objects are visible in tabs and configurable (with or without Setup). If user has no Setup, they still see and use only these objects.
                </Form.Text>
                <MultiSelectDropdown
                  selectedValues={allowedObjectsForDisplay(config.allowedObjectsPermission ?? config.objectManager?.allowedObjects ?? [])}
                  picklistValues={standardPicklistOptions}
                  onChange={handleAllowedObjectsPermissionChange}
                  disabled={isReadOnly}
                  placeholder="Select objects user can access"
                />
                <Form.Text className="text-muted small d-block mt-1">
                  Activity, Task, Note, Email log, Product, Product price, User, Role, and Password reset are always included in migration and are not shown here.
                </Form.Text>
              </Card>
            </Col>
          </Row>

          {config.setup.showSetup !== false && (
            <Row className="g-3">
              <Col xs={12}>
                <Card className="p-3">
                  <h5>Setup Configuration</h5>
                  <Form.Text className="d-block mb-3 text-muted small">
                    Configure Setup Menu first (which items appear in Setup). Then Tab, Field, Layout, Workflow (if allowed), Scoring Rule, Assignment Rule, Web Forms, Search Views, Lead Conversion and Migration, Object Manager, and Object Permissions. Report and Dashboard are configured separately below (not part of Setup).
                  </Form.Text>
                  <Row className="g-3">
                    <Col md={6}>
                      <Card className="p-3 bg-light">
                        <h6 className="mb-2">Setup Menu</h6>
                        <Form.Group className="mb-2">
                          <Form.Label>Mode</Form.Label>
                          <Form.Select
                            value={config.setup.mode}
                            onChange={(e) =>
                              setConfig((prev) => ({
                                ...prev,
                                setup: { ...prev.setup, mode: e.target.value },
                              }))
                            }
                            disabled={isReadOnly}
                          >
                            <option value="allowlist">allowlist</option>
                            <option value="denylist">denylist</option>
                          </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-2">
                          <Form.Label>Allowed setup items</Form.Label>
                          <MultiSelectDropdown
                            picklistValues={SETUP_OPTIONS}
                            selectedValues={config.setup.allowedItems ?? []}
                            onChange={(values) =>
                              setConfig((prev) => ({
                                ...prev,
                                setup: { ...prev.setup, allowedItems: values },
                              }))
                            }
                            disabled={isReadOnly}
                            placeholder="Select setup items"
                          />
                        </Form.Group>
                        <Form.Text className="text-muted small">Which setup menu items are available. If Workflow Manager is not selected here, the Workflow section below is hidden and workflow is disabled for this plan.</Form.Text>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="p-3 bg-light">
                        <h6 className="mb-2">App Manager</h6>
                        <Form.Text className="d-block mb-2 text-muted small">Create app permission and limit. −1 = unlimited.</Form.Text>
                        <Form.Group className="mb-2">
                          <Form.Label>Max Apps</Form.Label>
                          <Form.Control
                            type="number"
                            min={-1}
                            value={config.app?.maxApps ?? -1}
                            onChange={(e) => {
                              const v = e.target.value;
                              const num = v === '' ? -1 : parseInt(v, 10);
                              if (!Number.isFinite(num)) return;
                              setConfig((prev) => ({ ...prev, app: { ...(prev.app || {}), maxApps: num } }));
                            }}
                            disabled={isReadOnly}
                          />
                          <Form.Text className="text-muted small">−1 = unlimited</Form.Text>
                        </Form.Group>
                        <Form.Check type="switch" label="Allow Create App" checked={config.app?.allowCreateApp !== false} onChange={(e) => setConfig((prev) => ({ ...prev, app: { ...(prev.app || {}), allowCreateApp: e.target.checked } }))} disabled={isReadOnly} />
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="p-3 bg-light">
                        <h6 className="mb-2">Tab Configuration</h6>
                        <Form.Text className="d-block mb-2 text-muted small">
                          Standard tabs available for this plan. Tabs whose object matches Allowed Objects Permission are shown. By default, matching tabs are selected.
                        </Form.Text>
                        <Form.Group className="mb-2">
                          <Form.Label>Allowed Standard Tabs</Form.Label>
                          <MultiSelectDropdown
                            picklistValues={standardTabPicklistOptions}
                            selectedValues={tabAllowedTabsSelected}
                            onChange={(values) => {
                              const requiredSet = new Set(packageTabIds);
                              const merged = [...packageTabIds, ...values.filter((id) => !requiredSet.has(id))];
                              setConfig((prev) => ({ ...prev, tab: { ...prev.tab, allowedTabs: merged } }));
                            }}
                            disabled={isReadOnly}
                            placeholder="Select standard tabs..."
                          />
                          <Form.Text className="text-muted small">
                            {packageTabIds.length > 0
                              ? 'Tabs for enabled packages (e.g. WatConnect, Email Campaign) are always included and cannot be removed.'
                              : 'Select which standard tabs are visible.'}
                          </Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-2">
                          <Form.Label>Max Tabs</Form.Label>
                          <Form.Control
                            type="number"
                            min={-1}
                            value={config.tab.maxTabs}
                            onChange={(e) => {
                              const v = e.target.value;
                              const num = v === '' ? 0 : parseInt(v, 10);
                              if (!Number.isFinite(num)) return;
                              setConfig((prev) => ({ ...prev, tab: { ...prev.tab, maxTabs: num } }));
                            }}
                            disabled={isReadOnly}
                          />
                          <Form.Text className="text-muted small">−1 = unlimited</Form.Text>
                        </Form.Group>
                        <Form.Check type="switch" label="Allow Create" checked={config.tab.allowCreate} onChange={(e) => setConfig((prev) => ({ ...prev, tab: { ...prev.tab, allowCreate: e.target.checked } }))} disabled={isReadOnly} />
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="p-3 bg-light">
                        <h6 className="mb-2">Field Management</h6>
                        <Form.Group className="mb-2">
                          <Form.Label>Max Custom Fields</Form.Label>
                          <Form.Control
                            type="number"
                            value={config.field.maxCustom}
                            onChange={(e) =>
                              setConfig((prev) => ({
                                ...prev,
                                field: { ...prev.field, maxCustom: Number(e.target.value) || 0 },
                              }))
                            }
                            disabled={isReadOnly}
                          />
                        </Form.Group>
                        <Form.Check type="switch" label="Allow Create" checked={config.field.allowCreate} onChange={(e) => setConfig((prev) => ({ ...prev, field: { ...prev.field, allowCreate: e.target.checked } }))} disabled={isReadOnly} />
                        <Form.Check type="switch" label="Allow Standard Edit" checked={config.field.allowStandardEdit} onChange={(e) => setConfig((prev) => ({ ...prev, field: { ...prev.field, allowStandardEdit: e.target.checked } }))} disabled={isReadOnly} />
                        <Form.Check type="switch" label="Allow Standard Delete" checked={config.field.allowStandardDelete} onChange={(e) => setConfig((prev) => ({ ...prev, field: { ...prev.field, allowStandardDelete: e.target.checked } }))} disabled={isReadOnly} />
                        <Form.Check type="switch" label="Allow Edit Label" checked={config.field.allowEditLabel !== false} onChange={(e) => setConfig((prev) => ({ ...prev, field: { ...prev.field, allowEditLabel: e.target.checked } }))} disabled={isReadOnly} />
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="p-3 bg-light">
                        <h6 className="mb-2">Layout Settings</h6>
                        <Form.Group className="mb-2">
                          <Form.Label>Count Mode</Form.Label>
                          <Form.Select
                            value={config.layout.countMode}
                            onChange={(e) =>
                              setConfig((prev) => ({
                                ...prev,
                                layout: { ...prev.layout, countMode: e.target.value },
                              }))
                            }
                            disabled={isReadOnly}
                          >
                            <option value="pair">pair</option>
                            <option value="single">single</option>
                          </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-2">
                          <Form.Label>Max Layouts</Form.Label>
                          <Form.Control
                            type="number"
                            min={-1}
                            value={config.layout.maxLayouts}
                            onChange={(e) => {
                              const v = e.target.value;
                              const num = v === '' ? 0 : parseInt(v, 10);
                              if (!Number.isFinite(num)) return;
                              setConfig((prev) => ({ ...prev, layout: { ...prev.layout, maxLayouts: num } }));
                            }}
                            disabled={isReadOnly}
                          />
                          <Form.Text className="text-muted small">−1 = unlimited</Form.Text>
                        </Form.Group>
                        <Form.Check type="switch" label="Allow Create" checked={config.layout.allowCreate} onChange={(e) => setConfig((prev) => ({ ...prev, layout: { ...prev.layout, allowCreate: e.target.checked } }))} disabled={isReadOnly} />
                      </Card>
                    </Col>
                    {(config.setup.allowedItems ?? []).map((i) => String(i).toLowerCase()).includes('workflows') && (
                      <Col md={6}>
                        <Card className="p-3 bg-light">
                          <h6 className="mb-2">Workflow</h6>
                          <Form.Text className="d-block mb-2 text-muted small">Workflow Manager in Setup Menu gives view access. Plan controls max count and create permission only.</Form.Text>
                          <Form.Group className="mb-2">
                            <Form.Label>Max Workflows</Form.Label>
                            <Form.Control
                              type="number"
                              min={-1}
                              value={config.workflow?.maxWorkflows ?? 10}
                              onChange={(e) => {
                                const v = e.target.value;
                                const num = v === '' ? 0 : parseInt(v, 10);
                                if (!Number.isFinite(num)) return;
                                setConfig((prev) => ({ ...prev, workflow: { ...prev.workflow, maxWorkflows: num } }));
                              }}
                              disabled={isReadOnly}
                            />
                            <Form.Text className="text-muted small">−1 = unlimited</Form.Text>
                          </Form.Group>
                          <Form.Check type="switch" label="Allow Create Workflow" checked={config.workflow?.allowCreateWorkflow !== false} onChange={(e) => setConfig((prev) => ({ ...prev, workflow: { ...prev.workflow, allowCreateWorkflow: e.target.checked } }))} disabled={isReadOnly} />
                        </Card>
                      </Col>
                    )}
                    <Col md={6}>
                      <Card className="p-3 bg-light">
                        <h6 className="mb-2">Scoring Rules</h6>
                        <Form.Text className="d-block mb-2 text-muted small">Which objects can have scoring rules. Only objects in Allowed Objects Permission are listed.</Form.Text>
                        <Form.Check type="switch" label="Allow View Scoring Rules" checked={config.scoringRule?.allowViewScoringRule !== false} onChange={(e) => setConfig((prev) => ({ ...prev, scoringRule: { ...prev.scoringRule, allowViewScoringRule: e.target.checked } }))} disabled={isReadOnly} />
                        <Form.Check type="switch" label="Allow Create / Edit Scoring Rules" checked={config.scoringRule?.allowCreateScoringRule !== false} onChange={(e) => setConfig((prev) => ({ ...prev, scoringRule: { ...prev.scoringRule, allowCreateScoringRule: e.target.checked } }))} disabled={isReadOnly} />
                        <Form.Group className="mb-0 mt-2">
                          <Form.Label>Objects allowed for scoring rules</Form.Label>
                          <MultiSelectDropdown
                            picklistValues={scoringRulePicklistOptions}
                            selectedValues={config.scoringRule?.allowedObjects ?? []}
                            onChange={(values) => setConfig((prev) => ({ ...prev, scoringRule: { ...prev.scoringRule, allowedObjects: values } }))}
                            disabled={isReadOnly}
                            placeholder="Select objects..."
                          />
                        </Form.Group>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="p-3 bg-light">
                        <h6 className="mb-2">Assignment Rules</h6>
                        <Form.Text className="d-block mb-2 text-muted small">Which objects can have assignment rules. Only objects in Allowed Objects Permission are listed.</Form.Text>
                        <Form.Check type="switch" label="Allow View Assignment Rules" checked={config.assignmentRule?.allowViewAssignmentRule !== false} onChange={(e) => setConfig((prev) => ({ ...prev, assignmentRule: { ...prev.assignmentRule, allowViewAssignmentRule: e.target.checked } }))} disabled={isReadOnly} />
                        <Form.Check type="switch" label="Allow Create / Edit Assignment Rules" checked={config.assignmentRule?.allowCreateAssignmentRule !== false} onChange={(e) => setConfig((prev) => ({ ...prev, assignmentRule: { ...prev.assignmentRule, allowCreateAssignmentRule: e.target.checked } }))} disabled={isReadOnly} />
                        <Form.Group className="mb-0 mt-2">
                          <Form.Label>Objects allowed for assignment rules</Form.Label>
                          <MultiSelectDropdown
                            picklistValues={scoringRulePicklistOptions}
                            selectedValues={config.assignmentRule?.allowedObjects ?? []}
                            onChange={(values) => setConfig((prev) => ({ ...prev, assignmentRule: { ...prev.assignmentRule, allowedObjects: values } }))}
                            disabled={isReadOnly}
                            placeholder="Select objects..."
                          />
                        </Form.Group>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="p-3 bg-light">
                        <h6 className="mb-2">Web Forms</h6>
                        <Form.Text className="d-block mb-2 text-muted small">Which objects can have web forms. Only objects in Allowed Objects Permission are listed.</Form.Text>
                        <Form.Check type="switch" label="Allow View Web Forms" checked={config.webform?.allowViewWebform !== false} onChange={(e) => setConfig((prev) => ({ ...prev, webform: { ...prev.webform, allowViewWebform: e.target.checked } }))} disabled={isReadOnly} />
                        <Form.Check type="switch" label="Allow Create / Edit Web Forms" checked={config.webform?.allowCreateWebform !== false} onChange={(e) => setConfig((prev) => ({ ...prev, webform: { ...prev.webform, allowCreateWebform: e.target.checked } }))} disabled={isReadOnly} />
                        <Form.Group className="mb-0 mt-2">
                          <Form.Label>Objects allowed for web forms</Form.Label>
                          <MultiSelectDropdown
                            picklistValues={scoringRulePicklistOptions}
                            selectedValues={config.webform?.allowedObjects ?? []}
                            onChange={(values) => setConfig((prev) => ({ ...prev, webform: { ...prev.webform, allowedObjects: values } }))}
                            disabled={isReadOnly}
                            placeholder="Select objects..."
                          />
                        </Form.Group>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="p-3 bg-light">
                        <h6 className="mb-2">Search Views</h6>
                        <Form.Text className="d-block mb-2 text-muted small">Which objects can have search views configured. Only objects in Allowed Objects Permission are listed.</Form.Text>
                        <Form.Check type="switch" label="Allow View Search Views" checked={config.searchView?.allowViewSearchView !== false} onChange={(e) => setConfig((prev) => ({ ...prev, searchView: { ...prev.searchView, allowViewSearchView: e.target.checked } }))} disabled={isReadOnly} />
                        <Form.Check type="switch" label="Allow Create / Edit Search Views" checked={config.searchView?.allowCreateSearchView !== false} onChange={(e) => setConfig((prev) => ({ ...prev, searchView: { ...prev.searchView, allowCreateSearchView: e.target.checked } }))} disabled={isReadOnly} />
                        <Form.Group className="mb-0 mt-2">
                          <Form.Label>Objects allowed for search views</Form.Label>
                          <MultiSelectDropdown
                            picklistValues={scoringRulePicklistOptions}
                            selectedValues={config.searchView?.allowedObjects ?? []}
                            onChange={(values) => setConfig((prev) => ({ ...prev, searchView: { ...prev.searchView, allowedObjects: values } }))}
                            disabled={isReadOnly}
                            placeholder="Select objects..."
                          />
                        </Form.Group>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="p-3 bg-light">
                        <h6 className="mb-2">Lead Conversion and Migration</h6>
                        <Form.Check type="switch" label="Allow Mapping" checked={config.leadConversionMapping.allow} onChange={(e) => setConfig((prev) => ({ ...prev, leadConversionMapping: { ...prev.leadConversionMapping, allow: e.target.checked } }))} disabled={isReadOnly} />
                        <Form.Check type="switch" label="Data Migration" checked={config.migration.allow} onChange={(e) => setConfig((prev) => ({ ...prev, migration: { ...prev.migration, allow: e.target.checked } }))} disabled={isReadOnly} className="mt-2" />
                        <Form.Group className="mb-2 mt-2">
                          <Form.Label>Mode</Form.Label>
                          <Form.Select
                            value={config.leadConversionMapping.mode}
                            onChange={(e) =>
                              setConfig((prev) => ({
                                ...prev,
                                leadConversionMapping: { ...prev.leadConversionMapping, mode: e.target.value },
                              }))
                            }
                            disabled={isReadOnly}
                          >
                            <option value="allowlist">allowlist</option>
                            <option value="denylist">denylist</option>
                          </Form.Select>
                        </Form.Group>
                        <Form.Group className="mb-2">
                          <Form.Label>Max Objects</Form.Label>
                          <Form.Control
                            type="number"
                            value={config.leadConversionMapping.maxObjects}
                            onChange={(e) =>
                              setConfig((prev) => ({
                                ...prev,
                                leadConversionMapping: { ...prev.leadConversionMapping, maxObjects: Number(e.target.value) || 0 },
                              }))
                            }
                            disabled={isReadOnly}
                          />
                        </Form.Group>
                        <Form.Group className="mb-2">
                          <Form.Label>Objects for lead conversion</Form.Label>
                          <MultiSelectDropdown
                            picklistValues={leadConversionPicklistOptions}
                            selectedValues={config.leadConversionMapping.allowedObjects ?? ['account', 'contact', 'opportunity']}
                            onChange={(values) =>
                              setConfig((prev) => ({
                                ...prev,
                                leadConversionMapping: { ...prev.leadConversionMapping, allowedObjects: values },
                              }))
                            }
                            disabled={isReadOnly}
                            placeholder="Select objects..."
                          />
                        </Form.Group>
                        <Form.Text className="text-muted small">Choose which of the configured Allowed Objects Permission can be used for lead conversion. Default: Account, Contact, Opportunity.</Form.Text>
                      </Card>
                    </Col>
                    <Col md={6}>
                      <Card className="p-3 bg-light">
                        <h6 className="mb-2">Object Manager</h6>
                        <Form.Text className="d-block mb-2 text-muted small">
                          Uses Allowed Objects Permission above. Without Setup, users only see those objects (no add/remove fields or layout).
                        </Form.Text>
                        <Form.Check type="switch" label="Allow Create" checked={!!config.objectManager.allow_create} onChange={(e) => setConfig((prev) => ({ ...prev, objectManager: { ...prev.objectManager, allow_create: e.target.checked } }))} disabled={isReadOnly} className="mb-2" />
                        <Form.Check type="switch" label="Enable Standard" checked={!!config.objectManager.enable_standard} onChange={(e) => setConfig((prev) => ({ ...prev, objectManager: { ...prev.objectManager, enable_standard: e.target.checked } }))} disabled={isReadOnly} className="mb-2" />
                      </Card>
                    </Col>
                    <Col xs={12}>
                      <Card className="p-3 bg-light">
                        <h6 className="mb-2">Object Permissions</h6>
                        <Form.Text className="d-block mb-2 text-muted small">
                          Define per-object permissions (Create, Read, Update, Delete, View All, Modify All). Same UI as role PermissionBulkEdit. Plan licenses inherit and can restrict further.
                        </Form.Text>
                        <ObjectPermissionMatrix
                          objects={(config.allowedObjectsPermission ?? []).map((name) => {
                            const opt = standardPicklistOptions.find((o) => String(o.key).toLowerCase() === String(name).toLowerCase());
                            const displayLabel = opt?.value || (String(name).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || name);
                            return { object_name: name, object_display_name: displayLabel };
                          })}
                          permissions={config.objectPermissions ?? {}}
                          onChange={(objectPermissions) =>
                            setConfig((prev) => ({ ...prev, objectPermissions }))
                          }
                          disabled={isReadOnly}
                          showSearch={true}
                        />
                      </Card>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          )}

          <Row className="g-3">
            <Col md={6}>
              <Card className="p-3">
                <h5>Report</h5>
                <Form.Text className="d-block mb-2 text-muted small">Report is not part of Setup; configure regardless of Setup.</Form.Text>
                <Form.Group className="mb-2">
                  <Form.Label>Max Reports</Form.Label>
                  <Form.Control
                    type="number"
                    min={-1}
                    value={config.report?.maxReports ?? 10}
                    onChange={(e) => {
                      const v = e.target.value;
                      const num = v === '' ? 0 : parseInt(v, 10);
                      if (!Number.isFinite(num)) return;
                      setConfig((prev) => ({ ...prev, report: { ...prev.report, maxReports: num } }));
                    }}
                    disabled={isReadOnly}
                  />
                  <Form.Text className="text-muted small">−1 = unlimited</Form.Text>
                </Form.Group>
                <Form.Check type="switch" label="Allow Create Report" checked={config.report?.allowCreateReport !== false} onChange={(e) => setConfig((prev) => ({ ...prev, report: { ...prev.report, allowCreateReport: e.target.checked } }))} disabled={isReadOnly} />
              </Card>
            </Col>
            <Col md={6}>
              <Card className="p-3">
                <h5>Dashboard</h5>
                <Form.Text className="d-block mb-2 text-muted small">Dashboard is not part of Setup; configure regardless of Setup.</Form.Text>
                <Form.Group className="mb-2">
                  <Form.Label>Max Dashboards</Form.Label>
                  <Form.Control
                    type="number"
                    min={-1}
                    value={config.dashboard?.maxDashboards ?? 5}
                    onChange={(e) => {
                      const v = e.target.value;
                      const num = v === '' ? 0 : parseInt(v, 10);
                      if (!Number.isFinite(num)) return;
                      setConfig((prev) => ({ ...prev, dashboard: { ...prev.dashboard, maxDashboards: num } }));
                    }}
                    disabled={isReadOnly}
                  />
                  <Form.Text className="text-muted small">−1 = unlimited</Form.Text>
                </Form.Group>
                <Form.Check type="switch" label="Allow Create Dashboard" checked={config.dashboard?.allowCreateDashboard !== false} onChange={(e) => setConfig((prev) => ({ ...prev, dashboard: { ...prev.dashboard, allowCreateDashboard: e.target.checked } }))} disabled={isReadOnly} />
              </Card>
            </Col>
          </Row>

          <div className="mt-3 d-flex justify-content-end">
            {editMode ? (
              <>
                <Button className="admin-primary-btn" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button variant="secondary" onClick={handleCancel} disabled={saving} className="ms-2">
                  Cancel
                </Button>
              </>
            ) : (
              <Button className="admin-primary-btn" onClick={() => setEditMode(true)} disabled={loading || saving}>
                Edit
              </Button>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default AdminPlanConfig;
